import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useSEO } from '../../utils/seo';

export default function HomePage() {
  // SEO Configuration
  useSEO({
    title: 'Izislide - Création Présentations PowerPoint Professionnelles | Designer PowerPoint',
    description: 'Imagez vos idées avec Izislide. Designer PowerPoint expert en création de présentations PPT professionnelles. Transformez vos présentations PowerPoint en outils de persuasion. Livraison sous 48h.',
    keywords: 'présentation powerpoint, création ppt, designer powerpoint, design présentation, création powerpoint, agence powerpoint, présentation professionnelle',
    canonical: '/',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ProfessionalService',
      name: 'Izislide',
      description: 'Designer PowerPoint - Création de présentations PowerPoint professionnelles, formations design PPT et storytelling',
      url: import.meta.env.VITE_SITE_URL || 'https://example.com',
      logo: 'https://static.readdy.ai/image/857147547f7da499a0fc7d35aaf6d8ec/390d10369bbbb8f9b90e17aee7042be0.png',
      slogan: 'Imagez vos idées',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '123 Avenue des Champs-Élysées',
        addressLocality: 'Paris',
        postalCode: '75008',
        addressCountry: 'FR'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '48.8566',
        longitude: '2.3522'
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+33-1-23-45-67-89',
        contactType: 'Customer Service',
        email: 'contact@izislide.com',
        availableLanguage: 'French'
      },
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00'
      },
      sameAs: [
        'https://www.linkedin.com/company/izislide',
        'https://twitter.com/izislide',
        'https://www.facebook.com/izislide'
      ]
    }
  });

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const elements = document.querySelectorAll('.fade-in-section');
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8) {
          el.classList.add('visible');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-24 overflow-hidden bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0,168,107,0.1) 0%, rgba(255,255,255,0.9) 50%, rgba(247,163,72,0.1) 100%), url('https://public.readdy.ai/ai/img_res/d7ff33f9314d23fa2864ef4356feb439.jpg')`
        }}
      >        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              Des présentations PowerPoint qui <span className="text-[#00A86B]">marquent les esprits</span> et font passer vos idées
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-10 leading-relaxed">
              <strong>Imagez vos idées</strong> : Izislide transforme vos présentations PowerPoint en outils de persuasion clairs, visuels et professionnels grâce à nos designers PowerPoint experts.
            </p>
            <a 
              href="#segments" 
              onClick={(e) => scrollToSection(e, 'segments')}
              className="inline-block bg-[#00A86B] text-white px-10 py-4 rounded-lg text-lg font-semibold hover:bg-[#008f5c] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Trouvez votre solution
            </a>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="fade-in-section">
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=frustrated%20business%20person%20looking%20at%20boring%20ineffective%20presentation%20slides%20with%20charts%20graphs%20outdated%20design%20poor%20visual%20communication%20office%20meeting%20room%20professional%20photography%20natural%20lighting&width=600&height=400&seq=problem1&orientation=landscape"
                    alt="Problème présentation PowerPoint"
                    className="rounded-lg shadow-lg mb-6 w-full h-64 object-cover"
                  />
                  <div className="bg-red-50 border-l-4 border-red-400 p-8 rounded-lg">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Le problème</h3>
                    <p className="text-lg text-gray-700">
                      Vos présentations PowerPoint ternes coûtent de l'attention. Messages noyés dans vos PPT, visuels datés, audiences décrochées.
                    </p>
                  </div>
                </div>
              </div>
              <div className="fade-in-section">
                <div className="relative">
                  <img 
                    src="https://readdy.ai/api/search-image?query=engaging%20successful%20business%20presentation%20with%20modern%20professional%20slides%20clear%20visual%20design%20attentive%20audience%20conference%20room%20bright%20natural%20lighting%20green%20orange%20color%20accents%20high%20quality%20photography&width=600&height=400&seq=solution1&orientation=landscape"
                    alt="Solution présentation PowerPoint"
                    className="rounded-lg shadow-lg mb-6 w-full h-64 object-cover"
                  />
                  <div className="bg-[#C9D86A]/20 border-l-4 border-[#00A86B] p-8 rounded-lg">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Notre solution</h3>
                    <p className="text-lg text-gray-700">
                      Nos présentations PowerPoint captivent et convertissent. Design PPT impactant, messages clairs, résultats mesurables grâce à nos designers PowerPoint.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 Segments Section */}
      <section id="segments" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Qui accompagnons-nous ?
            </h2>
            <p className="text-xl text-gray-600">
              Des solutions de création PowerPoint sur mesure pour chaque besoin
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            <Link 
              to="/agences" 
              className="group bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-[#F7A348] hover:shadow-2xl transition-all duration-300 cursor-pointer"
            >
              <div className="h-32 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=creative%20advertising%20agency%20team%20working%20on%20client%20presentation%20slides%20modern%20office%20workspace%20collaborative%20environment%20bright%20natural%20lighting%20professional%20photography%20marketing%20materials&width=400&height=200&seq=agence1&orientation=landscape"
                  alt="Agences de communication événementielle"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-8">
                <div className="w-16 h-16 bg-[#F7A348]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#F7A348]/20 transition-all">
                  <i className="ri-briefcase-line text-3xl text-[#F7A348]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Agences</h3>
                <p className="text-gray-600 mb-4">
                  Un designer PowerPoint partenaire fiable pour vos présentations clients
                </p>
                <span className="text-[#00A86B] font-semibold group-hover:underline">
                  En savoir plus →
                </span>
              </div>
            </Link>

            <Link 
              to="/entreprises" 
              className="group bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-[#F7A348] hover:shadow-2xl transition-all duration-300 cursor-pointer"
            >
              <div className="h-32 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=corporate%20business%20meeting%20with%20professional%20presentation%20slides%20conference%20room%20executive%20team%20modern%20office%20environment%20clean%20design%20natural%20lighting%20high%20quality%20photography&width=400&height=200&seq=entreprise1&orientation=landscape"
                  alt="Entreprises et professionnels"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-8">
                <div className="w-16 h-16 bg-[#00A86B]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#00A86B]/20 transition-all">
                  <i className="ri-building-line text-3xl text-[#00A86B]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Entreprises</h3>
                <p className="text-gray-600 mb-4">
                  Déléguez vos présentations PowerPoint internes et gagnez du temps
                </p>
                <span className="text-[#00A86B] font-semibold group-hover:underline">
                  En savoir plus →
                </span>
              </div>
            </Link>

            <Link 
              to="/formateurs" 
              className="group bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-[#F7A348] hover:shadow-2xl transition-all duration-300 cursor-pointer"
            >
              <div className="h-32 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20trainer%20presenting%20educational%20content%20with%20engaging%20slides%20classroom%20training%20session%20modern%20learning%20environment%20interactive%20presentation%20natural%20lighting%20high%20quality%20photography&width=400&height=200&seq=formateur1&orientation=landscape"
                  alt="Formateurs et centres de formation"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-8">
                <div className="w-16 h-16 bg-[#C9D86A]/30 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#C9D86A]/50 transition-all">
                  <i className="ri-presentation-line text-3xl text-[#00A86B]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Formateurs</h3>
                <p className="text-gray-600 mb-4">
                  Supports PowerPoint pédagogiques clairs et engageants à grande échelle
                </p>
                <span className="text-[#00A86B] font-semibold group-hover:underline">
                  En savoir plus →
                </span>
              </div>
            </Link>

            <Link 
              to="/formations" 
              className="group bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-[#F7A348] hover:shadow-2xl transition-all duration-300 cursor-pointer"
            >
              <div className="h-32 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20training%20workshop%20learning%20presentation%20skills%20hands-on%20learning%20environment%20modern%20classroom%20setting%20bright%20natural%20lighting%20educational%20materials%20high%20quality%20photography&width=400&height=200&seq=formation1&orientation=landscape"
                  alt="Formations PowerPoint"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-8">
                <div className="w-16 h-16 bg-[#F7A348]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#F7A348]/20 transition-all">
                  <i className="ri-graduation-cap-line text-3xl text-[#F7A348]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Formations</h3>
                <p className="text-gray-600 mb-4">
                  Apprenez à créer vous-même des présentations PowerPoint qui convainquent
                </p>
                <span className="text-[#00A86B] font-semibold group-hover:underline">
                  En savoir plus →
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ils nous font confiance pour leurs présentations PowerPoint
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20business%20woman%20portrait%20smiling%20confident%20marketing%20director%20modern%20office%20environment%20natural%20lighting%20headshot%20photography%20clean%20background&width=100&height=100&seq=client1&orientation=squarish"
                  alt="Sophie Chen"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="ml-4">
                  <div className="font-bold text-gray-900">Sophie Chen</div>
                  <div className="text-sm text-gray-600">Directrice Marketing</div>
                </div>
              </div>
              <p className="text-gray-700 italic">
                "Izislide a transformé nos présentations PowerPoint commerciales. Nos taux de conversion ont augmenté de 40% grâce à leurs designers PowerPoint."
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20business%20man%20portrait%20smiling%20confident%20CEO%20creative%20agency%20director%20modern%20office%20environment%20natural%20lighting%20headshot%20photography%20clean%20background&width=100&height=100&seq=client2&orientation=squarish"
                  alt="Marc Lefebvre"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="ml-4">
                  <div className="font-bold text-gray-900">Marc Lefebvre</div>
                  <div className="text-sm text-gray-600">CEO Agence Créative</div>
                </div>
              </div>
              <p className="text-gray-700 italic">
                "Un partenaire fiable qui livre toujours des présentations PPT dans les délais. Nos clients adorent la qualité du design PowerPoint."
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20trainer%20woman%20portrait%20smiling%20confident%20educator%20training%20specialist%20modern%20learning%20environment%20natural%20lighting%20headshot%20photography%20clean%20background&width=100&height=100&seq=client3&orientation=squarish"
                  alt="Anne Dubois"
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="ml-4">
                  <div className="font-bold text-gray-900">Anne Dubois</div>
                  <div className="text-sm text-gray-600">Formatrice</div>
                </div>
              </div>
              <p className="text-gray-700 italic">
                "Mes supports PowerPoint de formation sont maintenant professionnels et mes stagiaires sont plus engagés. Imagez vos idées, c'est exactement ça !"
              </p>
            </div>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-12 opacity-40">
            <div className="text-2xl font-bold text-gray-600">TOTAL</div>
            <div className="text-2xl font-bold text-gray-600">ORANGE</div>
            <div className="text-2xl font-bold text-gray-600">BNP PARIBAS</div>
            <div className="text-2xl font-bold text-gray-600">SNCF</div>
            <div className="text-2xl font-bold text-gray-600">DECATHLON</div>
          </div>
        </div>
      </section>

      {/* Method Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Notre méthode de création PowerPoint en 3 étapes
            </h2>
            <p className="text-xl text-gray-600">
              Simple, rapide, efficace
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto">
            <div className="text-center fade-in-section">
              <div className="relative mb-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=business%20meeting%20briefing%20session%20client%20discussing%20project%20requirements%20modern%20office%20environment%20collaborative%20workspace%20natural%20lighting%20professional%20photography%20clean%20background&width=300&height=200&seq=etape1&orientation=landscape"
                  alt="Brief présentation PowerPoint"
                  className="w-full h-48 object-cover rounded-lg shadow-lg"
                />
                <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-20 h-20 bg-[#F7A348] rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-xl">
                  1
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Brief</h3>
              <p className="text-gray-600 text-lg">
                Vous nous partagez votre besoin de présentation PowerPoint, vos objectifs et votre contenu. Un échange de 30 minutes suffit.
              </p>
            </div>

            <div className="text-center fade-in-section">
              <div className="relative mb-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=creative%20design%20process%20presentation%20slides%20being%20created%20on%20computer%20screen%20design%20software%20professional%20workspace%20modern%20office%20natural%20lighting%20high%20quality%20photography&width=300&height=200&seq=etape2&orientation=landscape"
                  alt="Conception PowerPoint"
                  className="w-full h-48 object-cover rounded-lg shadow-lg"
                />
                <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-20 h-20 bg-[#00A86B] rounded-full flex items-center justify-center text-white text-3xl font-bold shadow-xl">
                  2
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Conception</h3>
              <p className="text-gray-600 text-lg">
                Nos designers PowerPoint experts créent votre présentation PPT impactante et sur mesure. <strong>Imagez vos idées</strong> devient réalité.
              </p>
            </div>

            <div className="text-center fade-in-section">
              <div className="relative mb-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=successful%20presentation%20delivery%20completed%20slides%20being%20presented%20to%20satisfied%20audience%20modern%20conference%20room%20professional%20environment%20bright%20natural%20lighting%20photography&width=300&height=200&seq=etape3&orientation=landscape"
                  alt="Livraison présentation PowerPoint"
                  className="w-full h-48 object-cover rounded-lg shadow-lg"
                />
                <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-20 h-20 bg-[#C9D86A] rounded-full flex items-center justify-center text-gray-900 text-3xl font-bold shadow-xl">
                  3
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Livraison rapide</h3>
              <p className="text-gray-600 text-lg">
                Vous recevez votre présentation PowerPoint finalisée sous 48-72h, prête à convaincre votre audience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section 
        className="py-24 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0,168,107,0.9) 0%, rgba(0,143,92,0.9) 100%), url('https://readdy.ai/api/search-image?query=modern%20professional%20business%20success%20presentation%20conference%20room%20with%20presentation%20slides%20successful%20meeting%20environment%20bright%20natural%20lighting%20high%20quality%20photography&width=1920&height=600&seq=cta1&orientation=landscape')`
        }}
      >
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Prêt à transformer vos présentations PowerPoint ?
            </h2>
            <p className="text-xl mb-10 opacity-90">
              Discutons de votre projet PPT et recevez un devis personnalisé sous 24h
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact" 
                className="inline-block bg-white text-[#00A86B] px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Demander un devis
              </Link>
              <Link 
                to="/a-propos" 
                className="inline-block bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-[#00A86B] transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                En savoir plus
              </Link>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .fade-in-section {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }
        .fade-in-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
}
